import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import Login from "../../app/auth/Login";
import AdminMainLayout from "../../components/layout/AdminMainLayout";
import { getToken } from "../../app/auth/authConfig";
import Tabs from "../../components/reuseable/Tabs";
import DataTable from "../../components/reuseable/DataTable";
import TableSkeletonRow from "../../components/reuseable/TableSkeletonRow";
import EditModal from "../../components/modal/EditModal";
import DeleteModal from "../../components/modal/DeleteModal";
import PaginationControls from "../../components/reuseable/PaginationControls";

const DrawerHeader = styled("div")(({ theme }) => ({
  ...theme.mixins.toolbar,
}));

const pageTabs = [
  "PAGE MANAGEMENT",
  "UPLOAD PAGE",
  "REACT APP MANAGEMENT",
  "UPLOAD REACT APP",
];
const MAX_UPLOAD_SIZE_BYTES = 3 * 1024 * 1024;
const MAX_REACT_ZIP_SIZE_BYTES = 30 * 1024 * 1024;
const PAGE_SIZE = 50;

const formatFileSize = (bytes) => {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size < 0) return "-";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDateTime = (isoString) => {
  if (!isoString) return "-";
  const parsed = new Date(isoString);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleString();
};

const PagesManager = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [pages, setPages] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedReactZipFile, setSelectedReactZipFile] = useState(null);
  const [reactToolName, setReactToolName] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingReactApp, setIsUploadingReactApp] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingReactApps, setIsLoadingReactApps] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteReactAppModal, setShowDeleteReactAppModal] = useState(false);
  const [selectedReactApp, setSelectedReactApp] = useState(null);
  const [isDeletingReactApp, setIsDeletingReactApp] = useState(false);
  const [editError, setEditError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFiles, setTotalFiles] = useState(0);
  const [search, setSearch] = useState("");
  const [reactApps, setReactApps] = useState([]);
  const [reactPage, setReactPage] = useState(1);
  const [reactTotalPages, setReactTotalPages] = useState(1);
  const [reactTotalApps, setReactTotalApps] = useState(0);
  const [reactSearch, setReactSearch] = useState("");

  const backendBase = useMemo(
    () => (process.env.REACT_APP_BACKEND_API_URL || "").replace(/\/$/, ""),
    []
  );

  const fetchPages = useCallback(async () => {
    try {
      setIsLoading(true);
      setError("");
      const token = await getToken();
      const response = await axios.get(`${backendBase}/api/pages`, {
        params: {
          page,
          pageSize: PAGE_SIZE,
          q: search,
          sortBy: "uploadedAt",
          sortOrder: "desc",
        },
        headers: { "oidc-token": token },
      });
      const payload = response.data;
      if (Array.isArray(payload)) {
        setPages(payload);
        setTotalPages(1);
        setTotalFiles(payload.length);
      } else {
        const items = Array.isArray(payload?.items) ? payload.items : [];
        const resolvedTotalPages = Number(payload?.totalPages) || 1;
        const resolvedPage = Number(payload?.page) || 1;
        setPages(items);
        setTotalPages(resolvedTotalPages);
        setTotalFiles(Number(payload?.total) || 0);
        if (resolvedPage !== page) {
          setPage(resolvedPage);
        }
      }
    } catch (err) {
      setError("Failed to load pages list");
    } finally {
      setIsLoading(false);
    }
  }, [backendBase, page, search]);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  const fetchReactApps = useCallback(async () => {
    try {
      setIsLoadingReactApps(true);
      const token = await getToken();
      const response = await axios.get(`${backendBase}/api/react-apps`, {
        params: {
          page: reactPage,
          pageSize: PAGE_SIZE,
          q: reactSearch,
        },
        headers: { "oidc-token": token },
      });

      const payload = response.data;
      const items = Array.isArray(payload?.items) ? payload.items : [];
      const resolvedTotalPages = Number(payload?.totalPages) || 1;
      const resolvedPage = Number(payload?.page) || 1;

      setReactApps(items);
      setReactTotalPages(resolvedTotalPages);
      setReactTotalApps(Number(payload?.total) || 0);
      if (resolvedPage !== reactPage) {
        setReactPage(resolvedPage);
      }
    } catch (_err) {
      setError("Failed to load React apps");
    } finally {
      setIsLoadingReactApps(false);
    }
  }, [backendBase, reactPage, reactSearch]);

  useEffect(() => {
    fetchReactApps();
  }, [fetchReactApps]);

  const onFileChange = (event) => {
    setSuccess("");
    setError("");
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
  };

  const onUpload = async () => {
    if (!selectedFile) {
      setError("Please choose an HTML file first");
      return;
    }

    if (selectedFile.size > MAX_UPLOAD_SIZE_BYTES) {
      setError("File size must be 3 MB or less");
      return;
    }

    const lowerName = selectedFile.name.toLowerCase();
    if (!(lowerName.endsWith(".html") || lowerName.endsWith(".htm"))) {
      setError("Only .html or .htm files are allowed");
      return;
    }

    try {
      setIsUploading(true);
      setError("");
      setSuccess("");

      const token = await getToken();
      const formData = new FormData();
      formData.append("file", selectedFile);

      await axios.post(`${backendBase}/api/pages/upload`, formData, {
        headers: {
          "oidc-token": token,
          "Content-Type": "multipart/form-data",
        },
      });

      setSuccess("Page uploaded successfully");
      setSelectedFile(null);
      await fetchPages();
    } catch (err) {
      const apiError = err?.response?.data?.error;
      setError(apiError || "Upload failed");
    } finally {
      setIsUploading(false);
    }
  };

  const onReactZipChange = (event) => {
    setSuccess("");
    setError("");
    const file = event.target.files?.[0] || null;
    setSelectedReactZipFile(file);
  };

  const onUploadReactApp = async () => {
    if (!selectedReactZipFile) {
      setError("Please choose a ZIP file first");
      return;
    }

    if (selectedReactZipFile.size > MAX_REACT_ZIP_SIZE_BYTES) {
      setError("ZIP file size must be 30 MB or less");
      return;
    }

    const zipName = selectedReactZipFile.name.toLowerCase();
    if (!zipName.endsWith(".zip")) {
      setError("Only .zip files are allowed");
      return;
    }

    const trimmedToolName = reactToolName.trim();
    if (!trimmedToolName) {
      setError("Tool name is required");
      return;
    }

    try {
      setIsUploadingReactApp(true);
      setError("");
      setSuccess("");

      const token = await getToken();
      const formData = new FormData();
      formData.append("file", selectedReactZipFile);
      formData.append("toolName", trimmedToolName);

      await axios.post(`${backendBase}/api/pages/upload-react`, formData, {
        headers: {
          "oidc-token": token,
          "Content-Type": "multipart/form-data",
        },
      });

      setSuccess(`React app ${trimmedToolName} uploaded successfully`);
      setSelectedReactZipFile(null);
      setReactToolName("");
      await fetchReactApps();
    } catch (err) {
      const apiError = err?.response?.data?.error;
      setError(apiError || "React app upload failed");
    } finally {
      setIsUploadingReactApp(false);
    }
  };

  const onEditPage = useCallback((page) => {
    setSuccess("");
    setError("");
    setEditError("");
    setSelectedPage(page);
    setShowEditModal(true);
  }, []);

  const onDeleteClick = useCallback((page) => {
    setSuccess("");
    setError("");
    setSelectedPage(page);
    setShowDeleteModal(true);
  }, []);

  const onDeletePage = async () => {
    if (!selectedPage?.name) return;

    try {
      setIsDeleting(true);
      setError("");
      setSuccess("");
      const token = await getToken();

      await axios.delete(
        `${backendBase}/api/pages/${encodeURIComponent(selectedPage.name)}`,
        {
          headers: { "oidc-token": token },
        }
      );

      setSuccess(`Deleted ${selectedPage.name} successfully`);
      setShowDeleteModal(false);
      setSelectedPage(null);
      await fetchPages();
    } catch (err) {
      const apiError = err?.response?.data?.error;
      setError(apiError || "Failed to delete page");
    } finally {
      setIsDeleting(false);
    }
  };

  const onDeleteReactAppClick = useCallback((app) => {
    setSuccess("");
    setError("");
    setSelectedReactApp(app);
    setShowDeleteReactAppModal(true);
  }, []);

  const onDeleteReactApp = async () => {
    if (!selectedReactApp?.toolName) return;

    try {
      setIsDeletingReactApp(true);
      setError("");
      setSuccess("");
      const token = await getToken();

      await axios.delete(
        `${backendBase}/api/react-apps/${encodeURIComponent(selectedReactApp.toolName)}`,
        {
          headers: { "oidc-token": token },
        }
      );

      setSuccess(`Deleted ${selectedReactApp.toolName} successfully`);
      setShowDeleteReactAppModal(false);
      setSelectedReactApp(null);
      await fetchReactApps();
    } catch (err) {
      const apiError = err?.response?.data?.error;
      setError(apiError || "Failed to delete React app");
    } finally {
      setIsDeletingReactApp(false);
    }
  };

  const onSavePageName = async (formData) => {
    if (!selectedPage?.name) return;

    const newName = (formData?.name || "").trim();
    if (!newName) {
      setEditError("File name cannot be empty");
      setError("File name cannot be empty");
      return;
    }

    const lowerName = newName.toLowerCase();
    if (!(lowerName.endsWith(".html") || lowerName.endsWith(".htm"))) {
      setEditError("Only .html or .htm file names are allowed");
      setError("Only .html or .htm file names are allowed");
      return;
    }

    try {
      setEditError("");
      setError("");
      setSuccess("");
      const token = await getToken();

      await axios.put(
        `${backendBase}/api/pages/${encodeURIComponent(selectedPage.name)}`,
        { newName },
        { headers: { "oidc-token": token } }
      );

      setSuccess(`Renamed ${selectedPage.name} to ${newName}`);
      setShowEditModal(false);
      setSelectedPage(null);
      setEditError("");
      await fetchPages();
    } catch (err) {
      const apiError = err?.response?.data?.error;
      setEditError(apiError || "Failed to update page");
      setError(apiError || "Failed to update page");
    }
  };

  const getPageUrl = useCallback((filename) => {
    return `${window.location.origin}/pages/${encodeURIComponent(filename)}`;
  }, []);

  const getReactAppUrl = useCallback((toolName) => {
    const pageHost = window.location.origin;
    return `${pageHost}/staticTools/${encodeURIComponent(toolName)}/`;
  }, []);

  const pageColumns = useMemo(
    () => [
      {
        key: "name",
        label: "File Name",
        cellStyle: { wordBreak: "break-word", fontWeight: 600, color: "#5e1783" },
      },
      {
        key: "uploadedBy",
        label: "Uploaded By",
        render: (row) => row.uploadedBy || "-",
      },
      {
        key: "uploadedAt",
        label: "Uploaded At",
        render: (row) => formatDateTime(row.uploadedAt),
      },
      {
        key: "size",
        label: "File Size",
        headerStyle: { textAlign: "right" },
        cellStyle: { textAlign: "right", whiteSpace: "nowrap" },
        render: (row) => formatFileSize(row.size),
      },
      {
        key: "actions",
        label: "Actions",
        headerStyle: { textAlign: "left" },
        cellStyle: { textAlign: "left", whiteSpace: "nowrap" },
        render: (row) => (
          <div className="flex items-center justify-start gap-2">
            <a
              href={getPageUrl(row.name)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block rounded-md border border-[#7d1fa3]/25 bg-white px-3 py-1 text-sm font-semibold text-[#7d1fa3] hover:border-[#E12AFB]/70"
            >
              View
            </a>
            <button
              type="button"
              onClick={() => onEditPage(row)}
              className="inline-block rounded-md border border-[#7d1fa3]/25 bg-white px-3 py-1 text-sm font-semibold text-[#7d1fa3] hover:border-[#E12AFB]/70"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={() => onDeleteClick(row)}
              className="inline-block rounded-md border border-red-300/80 bg-white px-3 py-1 text-sm font-semibold text-red-700 hover:border-red-500"
            >
              Delete
            </button>
          </div>
        ),
      },
    ],
    [getPageUrl, onDeleteClick, onEditPage]
  );

  const reactAppColumns = useMemo(
    () => [
      {
        key: "toolName",
        label: "Tool Name",
        cellStyle: { wordBreak: "break-word", fontWeight: 600, color: "#5e1783" },
      },
      {
        key: "uploadedBy",
        label: "Uploaded By",
        render: (row) => row.uploadedBy || "-",
      },
      {
        key: "uploadedAt",
        label: "Uploaded At",
        render: (row) => formatDateTime(row.uploadedAt),
      },
      {
        key: "size",
        label: "App Size",
        headerStyle: { textAlign: "right" },
        cellStyle: { textAlign: "right", whiteSpace: "nowrap" },
        render: (row) => formatFileSize(row.size),
      },
      {
        key: "actions",
        label: "Actions",
        headerStyle: { textAlign: "left" },
        cellStyle: { textAlign: "left", whiteSpace: "nowrap" },
        render: (row) => (
          <div className="flex items-center justify-start gap-2">
            <a
              href={getReactAppUrl(row.toolName)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block rounded-md border border-[#7d1fa3]/25 bg-white px-3 py-1 text-sm font-semibold text-[#7d1fa3] hover:border-[#E12AFB]/70"
            >
              View
            </a>
            <button
              type="button"
              onClick={() => onDeleteReactAppClick(row)}
              className="inline-block rounded-md border border-red-300/80 bg-white px-3 py-1 text-sm font-semibold text-red-700 hover:border-red-500"
            >
              Delete
            </button>
          </div>
        ),
      },
    ],
    [getReactAppUrl, onDeleteReactAppClick]
  );

  return (
    <>
      <AuthenticatedTemplate>
        <div className="h-[var(--app-height)] overflow-hidden">
          <Box className="flex h-full">
            <AdminMainLayout />

            <Box
              component="main"
              className="flex-1 bg-white px-6 py-4 overflow-hidden"
            >
              <DrawerHeader />
              <div className="h-[calc(100vh-124px)] overflow-auto rounded-2xl bg-[radial-gradient(circle_at_20%_0%,_#f7ddff_0%,_#f4f7ff_40%,_#ecfbff_100%)] p-6 sm:p-8">
                <div className="mx-auto">
                  <h1 className="text-2xl sm:text-3xl font-bold text-[#5e1783]">HTML Pages Manager</h1>
                  <p className="text-[#6f4a86] mt-2">Upload HTML files and open them under /pages/filename.html.</p>
                  <div className="mt-6">
                    <Tabs activeTab={activeTab} setActiveTab={setActiveTab} tabs={pageTabs} />
                  </div>

                  {success ? <p className="text-sm text-green-700 mt-3">{success}</p> : null}
                  {error ? <p className="text-sm text-red-600 mt-3">{error}</p> : null}

                  {activeTab === 0 ? (
                    <div className="mt-2 bg-white rounded-xl border border-[#7d1fa3]/15 p-4 sm:p-5">
                      <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <input
                          type="text"
                          placeholder="Search by file name or uploaded by"
                          value={search}
                          onChange={(event) => {
                            setSearch(event.target.value);
                            setPage(1);
                          }}
                          disabled={isLoading}
                          className="w-full sm:max-w-md rounded-lg border border-[#7d1fa3]/20 bg-white px-3 py-2 text-sm text-[#5e1783] disabled:opacity-70"
                        />
                        <div className="flex items-center justify-between sm:justify-end gap-3">
                          <span className="text-sm font-semibold text-[#6f4a86]">
                            Total Files: {totalFiles}
                          </span>
                          <PaginationControls
                            page={page}
                            totalPages={totalPages}
                            isLoading={isLoading}
                            onPrev={() => setPage((prev) => Math.max(1, prev - 1))}
                            onNext={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                          />
                        </div>
                      </div>

                      <div className="mt-3 overflow-x-auto">
                        <DataTable
                          columns={pageColumns}
                          rows={pages}
                          loading={isLoading}
                          skeletonRowCount={5}
                          emptyMessage={<span className="text-[#6f4a86]">No pages uploaded yet.</span>}
                          rowKey={(row, idx) => row.name || `page-row-${idx}`}
                          loadingRow={(idx, columns) => (
                            <TableSkeletonRow
                              idx={idx}
                              rowKeyPrefix="pages-manager-skeleton"
                              columnCount={columns.length}
                              actionColumnIndex={columns.length - 1}
                              cellWidths={["12rem", "8rem", "9rem", "8rem", "7rem"]}
                            />
                          )}
                          rowProps={(_, idx) => ({
                            style: {
                              background: idx % 2 === 0 ? "#bb1ccc0d" : "#f3f0f7",
                              borderRadius: "8px",
                              border: "1px solid #e5e5e5",
                            },
                          })}
                          tableStyle={{ minWidth: "1100px", tableLayout: "fixed" }}
                        />
                      </div>
                    </div>
                  ) : null}

                  {activeTab === 1 ? (
                    <div className="mt-2 bg-white rounded-xl border border-[#7d1fa3]/15 p-4 sm:p-5">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <input
                          type="file"
                          accept=".html,.htm,text/html"
                          onChange={onFileChange}
                          className="block w-full text-sm text-[#5e1783] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#E12AFB]/15 file:text-[#7d1fa3] file:font-semibold hover:file:bg-[#E12AFB]/25"
                        />
                        <button
                          type="button"
                          onClick={onUpload}
                          disabled={isUploading}
                          className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#7d1fa3] to-[#E12AFB] text-white font-semibold disabled:opacity-60"
                        >
                          {isUploading ? "Uploading..." : "Upload"}
                        </button>
                      </div>

                      {selectedFile ? <p className="text-sm text-[#6f4a86] mt-2">Selected: {selectedFile.name}</p> : null}
                      <p className="text-xs text-[#6f4a86] mt-2">Maximum file size: 3 MB</p>
                    </div>
                  ) : null}

                  {activeTab === 2 ? (
                    <div className="mt-2 bg-white rounded-xl border border-[#7d1fa3]/15 p-4 sm:p-5">
                      <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <input
                          type="text"
                          placeholder="Search by tool name or uploaded by"
                          value={reactSearch}
                          onChange={(event) => {
                            setReactSearch(event.target.value);
                            setReactPage(1);
                          }}
                          disabled={isLoadingReactApps}
                          className="w-full sm:max-w-md rounded-lg border border-[#7d1fa3]/20 bg-white px-3 py-2 text-sm text-[#5e1783] disabled:opacity-70"
                        />
                        <div className="flex items-center justify-between sm:justify-end gap-3">
                          <span className="text-sm font-semibold text-[#6f4a86]">
                            Total Apps: {reactTotalApps}
                          </span>
                          <PaginationControls
                            page={reactPage}
                            totalPages={reactTotalPages}
                            isLoading={isLoadingReactApps}
                            onPrev={() => setReactPage((prev) => Math.max(1, prev - 1))}
                            onNext={() => setReactPage((prev) => Math.min(reactTotalPages, prev + 1))}
                          />
                        </div>
                      </div>

                      <div className="mt-3 overflow-x-auto">
                        <DataTable
                          columns={reactAppColumns}
                          rows={reactApps}
                          loading={isLoadingReactApps}
                          skeletonRowCount={5}
                          emptyMessage={<span className="text-[#6f4a86]">No React apps uploaded yet.</span>}
                          rowKey={(row, idx) => row.toolName || `react-app-row-${idx}`}
                          loadingRow={(idx, columns) => (
                            <TableSkeletonRow
                              idx={idx}
                              rowKeyPrefix="react-apps-manager-skeleton"
                              columnCount={columns.length}
                              actionColumnIndex={columns.length - 1}
                              cellWidths={["10rem", "8rem", "8rem", "9rem", "7rem"]}
                            />
                          )}
                          rowProps={(_, idx) => ({
                            style: {
                              background: idx % 2 === 0 ? "#bb1ccc0d" : "#f3f0f7",
                              borderRadius: "8px",
                              border: "1px solid #e5e5e5",
                            },
                          })}
                          tableStyle={{ minWidth: "1100px", tableLayout: "fixed" }}
                        />
                      </div>
                    </div>
                  ) : null}

                  {activeTab === 3 ? (
                    <div className="mt-2 bg-white rounded-xl border border-[#7d1fa3]/15 p-4 sm:p-5">
                      <div className="flex flex-col gap-3">
                        <input
                          type="text"
                          value={reactToolName}
                          onChange={(event) => setReactToolName(event.target.value)}
                          placeholder="Tool name (URL slug, e.g. sales_dashboard)"
                          className="w-full sm:max-w-md rounded-lg border border-[#7d1fa3]/20 bg-white px-3 py-2 text-sm text-[#5e1783]"
                        />
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                          <input
                            type="file"
                            accept=".zip,application/zip,application/x-zip-compressed"
                            onChange={onReactZipChange}
                            className="block w-full text-sm text-[#5e1783] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#E12AFB]/15 file:text-[#7d1fa3] file:font-semibold hover:file:bg-[#E12AFB]/25"
                          />
                          <button
                            type="button"
                            onClick={onUploadReactApp}
                            disabled={isUploadingReactApp}
                            className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#7d1fa3] to-[#E12AFB] text-white font-semibold disabled:opacity-60"
                          >
                            {isUploadingReactApp ? "Uploading..." : "Upload ZIP"}
                          </button>
                        </div>
                      </div>

                      {selectedReactZipFile ? (
                        <p className="text-sm text-[#6f4a86] mt-2">Selected: {selectedReactZipFile.name}</p>
                      ) : null}
                      <p className="text-xs text-[#6f4a86] mt-2">Maximum ZIP size: 30 MB</p>
                    </div>
                  ) : null}
                </div>
              </div>
            </Box>
          </Box>
        </div>
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <Login />
      </UnauthenticatedTemplate>

      {showEditModal && selectedPage ? (
        <EditModal
          title="Edit Page"
          fields={[{ label: "File Name", key: "name" }]}
          data={{ name: selectedPage.name }}
          errorMessage={editError}
          onClose={() => {
            setShowEditModal(false);
            setSelectedPage(null);
            setEditError("");
          }}
          onSave={onSavePageName}
        />
      ) : null}

      {showDeleteModal && selectedPage ? (
        <DeleteModal
          title={`Are you sure you want to delete ${selectedPage.name}?`}
          onClose={() => {
            if (isDeleting) return;
            setShowDeleteModal(false);
            setSelectedPage(null);
          }}
          onConfirm={onDeletePage}
          isLoading={isDeleting}
        />
      ) : null}

      {showDeleteReactAppModal && selectedReactApp ? (
        <DeleteModal
          title={`Are you sure you want to delete ${selectedReactApp.toolName}?`}
          onClose={() => {
            if (isDeletingReactApp) return;
            setShowDeleteReactAppModal(false);
            setSelectedReactApp(null);
          }}
          onConfirm={onDeleteReactApp}
          isLoading={isDeletingReactApp}
        />
      ) : null}
    </>
  );
};

export default PagesManager;
